import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Send, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["open", "all", "pending", "confirmed", "completed", "cancelled"];
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminInboxPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [msgs]);

  const load = useCallback(async (keepId) => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { params: { status: filter } });
      const list = data.bookings || [];
      setRows(list);
      const next = list.find((r) => r.booking_id === keepId) || list[0] || null;
      setSelected(next);
    } catch (e) {
      if (e?.response?.status === 403) navigate("/account");
      setRows([]);
    }
  }, [filter, navigate]);

  useEffect(() => {
    load(selected?.booking_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (!selected) { setMsgs(null); return; }
    setMsgs(null);
    axios.get(`${API}/bookings/${selected.booking_id}/messages`)
      .then(({ data }) => setMsgs(data.messages || []))
      .catch(() => setMsgs([]));
  }, [selected]);

  const reply = async () => {
    const text = body.trim();
    if (!text || !selected) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${selected.booking_id}/messages`, { body: text });
      setMsgs((m) => [...(m || []), data]);
      setBody("");
      setRows((rs) => rs.map((r) => (r.booking_id === selected.booking_id ? { ...r, needs_reply: false, message_count: (r.message_count || 0) + 1 } : r)));
    } catch {
      toast.error(t("vc.acct.thread.fail"));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${selected.booking_id}`, { status });
      setSelected((s) => ({ ...s, status: data.status }));
      setRows((rs) => rs.map((r) => (r.booking_id === data.booking_id ? { ...r, status: data.status } : r)));
      toast.success(t("vc.adm.inbox.status-saved"));
    } catch {
      toast.error(t("vc.adm.inbox.status-fail"));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white marble-bg" data-testid="admin-inbox-page">
      <Seo title={t("vc.adm.inbox.title")} noindex />

      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 h-[54px] flex items-center justify-between">
          <button onClick={() => navigate("/account")} className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-white transition-colors" data-testid="inbox-back">
            <ArrowLeft size={14} /> {t("vc.common.back")}
          </button>
          <div className="font-serif text-[15px]">Vangard<span className="text-neutral-500">Club</span></div>
          <LangSwitcher />
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 pt-20 pb-10">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[9px] tracking-[0.35em] uppercase text-neutral-500">Admin</div>
            <h1 className="font-serif text-2xl mt-1.5 leading-tight">{t("vc.adm.inbox.title")}</h1>
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500" data-testid="inbox-count">
            {rows?.length ?? 0}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`inbox-filter-${f}`}
              className={`text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border transition-colors ${
                filter === f ? "bg-white text-black border-white" : "border-white/[0.12] text-neutral-400 hover:border-white/35 hover:text-white"
              }`}
            >
              {t(`vc.adm.inbox.f-${f}`)}
            </button>
          ))}
        </div>

        <div className="mt-5 grid lg:grid-cols-[360px_1fr] gap-4">
          {/* list */}
          <div className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 overflow-hidden max-h-[70vh] overflow-y-auto no-scrollbar" data-testid="inbox-list">
            {rows === null ? (
              <div className="p-6 flex items-center gap-2 text-neutral-500 text-sm"><Loader2 size={15} className="animate-spin" /> {t("common.loading")}</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-neutral-500 text-sm" data-testid="inbox-empty">
                <Inbox size={20} className="mx-auto mb-3 opacity-50" />
                {t("vc.adm.inbox.empty")}
              </div>
            ) : rows.map((r) => {
              const on = selected?.booking_id === r.booking_id;
              return (
                <button
                  key={r.booking_id}
                  onClick={() => setSelected(r)}
                  data-testid={`inbox-row-${r.booking_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/[0.05] transition-colors ${on ? "bg-white/[0.06]" : "hover:bg-white/[0.02]"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] truncate">{r.name || r.email}</span>
                    {r.needs_reply && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-white" data-testid={`inbox-flag-${r.booking_id}`} />}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5 capitalize truncate">
                    {(r.service_type || "").replace(/[-_]/g, " ")} · {r.status}
                  </div>
                  <div className="text-[10px] text-neutral-600 mt-1">
                    {new Date(r.activity_at || r.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    {r.message_count ? ` · ${r.message_count}` : ""}
                  </div>
                </button>
              );
            })}
          </div>

          {/* thread */}
          <div className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 flex flex-col min-h-[420px]" data-testid="inbox-thread">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-neutral-600 text-sm">{t("vc.adm.inbox.pick")}</div>
            ) : (
              <>
                <div className="px-5 py-3.5 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm truncate" data-testid="inbox-thread-member">{selected.name || selected.email}</div>
                    <div className="text-[11px] text-neutral-500 truncate">{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(s)}
                        data-testid={`inbox-status-${s}`}
                        className={`text-[9px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border transition-colors ${
                          selected.status === s ? "bg-white text-black border-white" : "border-white/[0.14] text-neutral-400 hover:border-white/40 hover:text-white"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex-1 p-5 space-y-2.5 overflow-y-auto no-scrollbar max-h-[46vh]">
                  <Bubble side="member" name={selected.name || t("vc.acct.thread.you")} at={selected.created_at} body={selected.message} />
                  {selected.preferred_date && (
                    <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-600 text-center">
                      {t("vc.acct.req.preferred")}: {selected.preferred_date}
                    </div>
                  )}
                  {msgs === null ? (
                    <div className="flex items-center gap-2 text-neutral-500 text-[12px]"><Loader2 size={13} className="animate-spin" /> {t("common.loading")}</div>
                  ) : msgs.map((m) => (
                    <Bubble
                      key={m.message_id}
                      side={m.author}
                      name={m.author === "concierge" ? t("vc.acct.thread.concierge") : (selected.name || m.author_name)}
                      at={m.created_at}
                      body={m.body}
                    />
                  ))}
                  <div ref={endRef} />
                </div>

                <div className="p-4 border-t border-white/[0.06] flex items-end gap-2">
                  <textarea
                    rows={1}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(); } }}
                    placeholder={t("vc.adm.inbox.reply-ph")}
                    data-testid="inbox-reply-input"
                    className="flex-1 bg-transparent border border-white/[0.12] rounded-2xl focus:border-white/45 outline-none py-2.5 px-4 text-[13px] font-light resize-none transition-colors placeholder:text-neutral-600"
                  />
                  <button
                    onClick={reply}
                    disabled={sending || !body.trim()}
                    data-testid="inbox-reply-send"
                    className="shrink-0 inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-neutral-200 disabled:opacity-40 transition-colors"
                  >
                    {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {t("vc.acct.thread.send")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const Bubble = ({ side, name, at, body }) => {
  const concierge = side === "concierge";
  return (
    <div className={`flex ${concierge ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${concierge ? "bg-white text-black" : "bg-white/[0.05] text-neutral-200 border border-white/[0.08]"}`}>
        <div className={`text-[8px] tracking-[0.25em] uppercase ${concierge ? "text-neutral-600" : "text-neutral-500"}`}>
          {name} · {new Date(at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
        <p className="text-[13px] font-light leading-relaxed mt-1 whitespace-pre-wrap">{body}</p>
      </div>
    </div>
  );
};
